import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchVariance, fetchCostChecklist, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// F&B Cost Control — the controller's home / cockpit. Today's key numbers at a
// glance, a nudge when a day's usage still needs confirming, and quick links
// into their daily tasks. Controllers land here on login.
const MS = 1000;
const ET_MS = 3 * 3600 * MS;
const localStr = (ms) => new Date(ms + ET_MS).toISOString().slice(0, 16);
const startOfLocalDay = (ms) => localStr(ms).slice(0, 10) + "T00:00";

// Local trilingual strings for the daily checklist (kept out of the global
// bundle since they're specific to this card).
const CL = {
  en: {
    checklist: "Today's checklist", step_issue: "Morning issue", step_usage: "Usage log", step_stocktake: "Stocktake", open: "Open",
    s_issue_todo: "Not registered yet", s_issue_partial: "Registered — awaiting chef confirm", s_issue_done: "Registered & confirmed",
    s_usage_todo: "Not logged yet", s_usage_partial: "Logged — needs confirming", s_usage_done: "Logged & confirmed",
    s_stock_todo: "No stocktake yet", s_stock_recent: "Counted {n} days ago", s_stock_old: "Counted {n} days ago — due a recount",
  },
  am: {
    checklist: "የዛሬ ዝርዝር", step_issue: "የጠዋት ማውጫ", step_usage: "የአጠቃቀም መዝገብ", step_stocktake: "የክምችት ቆጠራ", open: "ክፈት",
    s_issue_todo: "ገና አልተመዘገበም", s_issue_partial: "ተመዝግቧል — የሼፍ ማረጋገጫ ይጠበቃል", s_issue_done: "ተመዝግቦ ተረጋግጧል",
    s_usage_todo: "ገና አልተመዘገበም", s_usage_partial: "ተመዝግቧል — ማረጋገጫ ይፈልጋል", s_usage_done: "ተመዝግቦ ተረጋግጧል",
    s_stock_todo: "ገና ቆጠራ የለም", s_stock_recent: "ከ{n} ቀናት በፊት ተቆጥሯል", s_stock_old: "ከ{n} ቀናት በፊት ተቆጥሯል — እንደገና ይቆጠር",
  },
  om: {
    checklist: "Sararaa har'aa", step_issue: "Baasii ganamaa", step_usage: "Galmee itti fayyadama", step_stocktake: "Lakkoofsa kuusaa", open: "Bani",
    s_issue_todo: "Ammayyuu hin galmoofne", s_issue_partial: "Galmaa'eera — mirkaneessa cheefii eeggachaa", s_issue_done: "Galmaa'ee mirkanaa'eera",
    s_usage_todo: "Ammayyuu hin galmoofne", s_usage_partial: "Galmaa'eera — mirkaneessa barbaada", s_usage_done: "Galmaa'ee mirkanaa'eera",
    s_stock_todo: "Ammayyuu lakkoofsi hin jiru", s_stock_recent: "Guyyoota {n} dura lakkaa'ame", s_stock_old: "Guyyoota {n} dura lakkaa'ame — irra deebi'ee lakkaa'amuu qaba",
  },
};

function steps(cl, ct) {
  const DONE = { mark: "✓", bg: "#E1F5EE", fg: "#0F6E56" };
  const WARN = { mark: "!", bg: "#FAEEDA", fg: "#8A5A0B" };
  const TODO = { mark: "○", bg: "#F1EFE8", fg: "#5F5E5A" };
  const iss = cl.issue || {}, us = cl.usage || {}, st = cl.stocktake || {};
  const ds = st.days_since;
  const stState = ds == null ? TODO : ds <= 7 ? DONE : WARN;
  const stStatus = ds == null ? ct("s_stock_todo")
    : (ds <= 7 ? ct("s_stock_recent") : ct("s_stock_old")).replace("{n}", ds);
  return [
    { to: "/kitchen-issue", title: ct("step_issue"),
      ...(iss.confirmed ? DONE : iss.exists ? WARN : TODO),
      status: iss.confirmed ? ct("s_issue_done") : iss.exists ? ct("s_issue_partial") : ct("s_issue_todo") },
    { to: "/usage", title: ct("step_usage"),
      ...(us.confirmed ? DONE : us.logged ? WARN : TODO),
      status: us.confirmed ? ct("s_usage_done") : us.logged ? ct("s_usage_partial") : ct("s_usage_todo") },
    { to: "/stocktake", title: ct("step_stocktake"), ...stState, status: stStatus },
  ];
}

export default function ControllerHomePage() {
  const { t, lang } = useT();
  const ct = (k) => (CL[lang] && CL[lang][k]) || CL.en[k] || k;
  const cur = getCurrency() || "ETB";
  const money = (v) => formatPrice(v || 0, cur);
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const now = Date.now();
    fetchVariance({ start: startOfLocalDay(now), end: localStr(now) })
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || t("cc_could_not_load")))
      .finally(() => setLoading(false));
    fetchCostChecklist().then(setChecklist).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tot = data && data.totals;
  const pctColor = (p) => p == null ? "#5f6b78" : p > 40 ? "#A32D2D" : p > 33 ? "#854F0B" : "#1D9E75";
  const foodCost = tot ? (tot.actual_entered ? tot.actual_cogs : tot.theoretical_cogs) : 0;
  const gross = tot ? (tot.revenue || 0) - (foodCost || 0) : 0;
  const grossPct = tot && tot.revenue ? Math.round((gross / tot.revenue) * 1000) / 10 : null;

  const actions = [
    { to: "/kitchen-issue", label: t("cc_nav_issue"), icon: "📦" },
    { to: "/recipes", label: t("cc_nav_recipes"), icon: "📖" },
    { to: "/ingredients", label: t("cc_nav_ingredients"), icon: "🥕" },
    { to: "/cost-variance", label: t("cc_nav_variance"), icon: "📊" },
  ];

  return (
    <>
      <h1>📊 {t("cc_section")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 640 }}>{t("cc_home_sub")}</p>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {tot && tot.actual_entered && !tot.usage_confirmed && (
        <Link to="/usage" className="card" style={{ display: "block", textDecoration: "none", marginTop: 12,
          padding: 14, background: "#fff8ec", border: "1px solid #f0d38b", color: "#854F0B", fontWeight: 600, maxWidth: 920 }}>
          ⏳ {t("cc_pending_confirm")} →
        </Link>
      )}

      {loading ? (
        <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p>
      ) : tot ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: 12, marginTop: 12, maxWidth: 920 }}>
          <Kpi label={t("cc_variance_cost")} value={tot.actual_entered ? money(tot.variance_cost) : "—"} color={tot.variance_cost > 0 ? "#A32D2D" : "#1D9E75"} />
          <Kpi label={t("cc_actual_food")} value={tot.actual_entered && tot.actual_food_cost_pct != null ? `${tot.actual_food_cost_pct}%` : "—"} color={pctColor(tot.actual_food_cost_pct)} />
          <Kpi label={t("cc_loss")} value={tot.returned_entered ? money(tot.loss_cogs) : "—"} color={tot.loss_cogs > 0 ? "#A32D2D" : "#1D9E75"} />
          <Kpi label={t("cc_gross_profit")} value={money(gross)} sub={grossPct == null ? null : `${grossPct}%`} color="#1D9E75" />
          <Kpi label={t("cc_revenue")} value={money(tot.revenue)} />
          <Kpi label={t("cc_orders")} value={tot.orders ?? 0} />
        </div>
      ) : null}

      {checklist && (
        <>
          <h3 style={{ marginTop: 22, marginBottom: 8 }}>{ct("checklist")}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 920 }}>
            {steps(checklist, ct).map((s) => (
              <Link key={s.to} to={s.to} className="card" style={{ display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", textDecoration: "none", color: "#063b48", border: "1px solid #e6ecec" }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", flex: "0 0 auto", background: s.bg, color: s.fg,
                               display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>{s.mark}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14 }}>
                  <b>{s.title}</b> <span className="muted" style={{ fontSize: 13 }}>— {s.status}</span>
                </span>
                <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{ct("open")} ›</span>
              </Link>
            ))}
          </div>
        </>
      )}

      <h3 style={{ marginTop: 22, marginBottom: 8 }}>{t("cc_quick_actions")}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px,1fr))", gap: 12, maxWidth: 920 }}>
        {actions.map((a) => (
          <Link key={a.to} to={a.to} className="card" style={{ textDecoration: "none", padding: 16, display: "flex", alignItems: "center", gap: 10, color: "#063b48", fontWeight: 600 }}>
            <span style={{ fontSize: 22 }}>{a.icon}</span> {a.label}
          </Link>
        ))}
      </div>
    </>
  );
}

function Kpi({ label, value, color, sub }) {
  return (
    <div className="card" style={{ padding: "12px 14px" }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || "#063b48" }}>{value}</div>
      {sub != null && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
